import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { WizardChrome, WizardFooter } from './WizardLayout'
import { connectionFor, detailOf, pathFor, stepStatus } from './steps'

const PROVIDER = 'linkedin'

/**
 * Step 3 — S-D2's `s3` and `s3-fail`.
 *
 * **The risk is stated in plain language and the limits are numbers.** Automating
 * LinkedIn breaches its User Agreement and accounts get restricted; a tenant who
 * is surprised by that later is a tenant AugRank misled. The three figures are
 * the ones CLAUDE.md hard-enforces in code — 50 searches, 20 actions, and zero
 * retries after a challenge — so they are a promise, not marketing.
 *
 * The checkbox gates the button *and* the server refuses without it. Both, on
 * purpose: the button is the courtesy and the server check is the rule, and §8
 * needs the acknowledgement recorded with the version of the text that was
 * shown — which only the server can do.
 *
 * A `challenge` outcome is the screen that matters most. AugRank stopped and did
 * not try again, and this says so in those words, because the reason accounts
 * get banned is retrying through a checkpoint.
 */
export function LinkedInStep(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const connection = connectionFor(state, PROVIDER)
  const step = stepStatus(state, 'linkedin')
  const [acknowledged, setAcknowledged] = useState(connection?.acknowledged_at != null)

  const connect = useMutation((body: { acknowledged: boolean }) =>
    call(() => api.POST('/api/v1/onboarding/linkedin/connect', { body })),
  )

  async function onConnect(): Promise<void> {
    const result = await connect.run({ acknowledged })
    if (!result.ok) return
    adopt(result.data.state)
    if (result.data.redirect_url) {
      window.location.assign(result.data.redirect_url)
      return
    }
    if (result.data.status === 'connected') void navigate(pathFor(result.data.state.current_step))
  }

  if (connection?.status === 'challenge') {
    return (
      <div className="app-wiz">
        {/* ONB-32. S-D2's `s3-fail` keeps the rail and drops the meter — the same
            argument the Search Console failure makes: a progress bar above a card
            saying the step was interrupted answers a question nobody is asking. */}
        <WizardChrome current="linkedin" meter={false} />
        <Challenge
          detail={connection.last_error}
          pausedAt={connection.paused_at}
          busy={connect.pending}
          onReconnect={() => {
            void onConnect()
          }}
          onContinue={() => {
            void navigate(pathFor('sender'))
          }}
        />
      </div>
    )
  }

  return (
    <div className="app-wiz">
      <WizardChrome current="linkedin" />

      <section className="ar-card">
        <div className="ar-card__body ar-stack">
          <div>
            <h2 className="ar-h2">Connect LinkedIn for lead discovery</h2>
            <p className="ar-muted" style={{ margin: 'var(--ar-space-2) 0 0' }}>
              Entirely optional. SEO works without it — this only powers outreach.
            </p>
          </div>

          <InlineError failure={connect.state.failure} />

          <div className="ar-alert ar-alert--warning">
            {/* ONB-34. `⚠` cautions; `✕` refuses; `!` stands in for a screen.
                Settled for the product by SF-16's AUTH-36. */}
            <span className="ar-alert__icon" aria-hidden="true">
              ⚠
            </span>
            <div className="ar-alert__body">
              <strong>Read this before connecting</strong>
              Automating LinkedIn breaches LinkedIn&rsquo;s User Agreement. Accounts can be
              restricted or permanently banned. We reduce the risk but cannot remove it.
            </div>
          </div>

          <div className="ar-panel ar-stack ar-stack--tight">
            <strong>What we enforce, in code</strong>
            <div className="ar-grid ar-grid--3">
              <Limit value="50" label="searches / day max" />
              <Limit value="20" label="actions / day max" />
              <Limit value="0" label="retries after a challenge" />
            </div>
            <p
              className="ar-muted"
              style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-body-sm-size)' }}
            >
              Randomised human-like delays. A dedicated residential IP for your account only. If
              LinkedIn shows any checkpoint, we pause immediately and email you — we never try to
              work through it.
            </p>
          </div>

          <label className="ar-choice ar-choice--check">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => {
                setAcknowledged(event.target.checked)
                connect.reset()
              }}
            />
            <span className="ar-choice__box" aria-hidden="true" />
            <span className="ar-choice__text">
              I understand this breaches LinkedIn&rsquo;s terms and that my account could be
              restricted or banned. I accept that risk.
              <span className="ar-choice__desc">
                Required. We record the timestamp and store it with your account.
              </span>
            </span>
          </label>

          {connection?.status === 'connected' ? (
            <div className="ar-alert ar-alert--success" role="status">
              <span className="ar-alert__icon" aria-hidden="true">
                ✓
              </span>
              <div className="ar-alert__body">
                <strong>Connected</strong>
                {connection.account_label ?? 'Lead discovery is ready to run.'}
              </div>
            </div>
          ) : step?.status === 'failed' && detailOf(step).message ? (
            <div className="ar-alert ar-alert--danger" role="alert">
              {/* ONB-35. A refusal, not a caution (ONB-34). */}
              <span className="ar-alert__icon" aria-hidden="true">
                ✕
              </span>
              <div className="ar-alert__body">
                <strong>We could not connect LinkedIn</strong>
                {String(detailOf(step).message)}
              </div>
            </div>
          ) : null}
        </div>

        <WizardFooter current="linkedin">
          {/* ONB-44. `aria-disabled` rather than `disabled`, which is what the
              Atlas layer draws (`onboarding.html:285`) and what `Button`'s
              `unavailable` prop exists for. `disabled` removes the control from
              the tab order, so a screen-reader user tabbing this card never met
              the primary action at all — they could not hear that it exists, let
              alone that it is waiting on the checkbox two rows up. Focusable,
              announced as unavailable, and inert either way. */}
          <Button
            tone="primary"
            disabled={!acknowledged}
            unavailable
            busy={connect.pending}
            busyLabel="Connecting LinkedIn"
            onClick={() => {
              void onConnect()
            }}
          >
            Connect LinkedIn
          </Button>
        </WizardFooter>
      </section>
    </div>
  )
}

function Limit({ value, label }: { value: string; label: string }): JSX.Element {
  return (
    <div>
      <div className="ar-h2 ar-num">{value}</div>
      <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
        {label}
      </span>
    </div>
  )
}

/**
 * The time a challenge landed, as S-D2 writes it: "14:22 IST" (ONB-10).
 *
 * `Asia/Kolkata` explicitly rather than the browser's zone — CLAUDE.md's rule
 * that all times use it, and here it is also what makes the "IST" beside the
 * number true. A tenant reading this in Dubai should see the same clock their
 * account manager quotes at them, not a local rendering of it.
 */
function pausedClock(value: string): string | null {
  const at = new Date(value)
  if (Number.isNaN(at.getTime())) return null
  return `${at.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata',
  })} IST`
}

/** S-D2's `s3-fail`. Paused, never retried — that is the whole message. */
function Challenge({
  detail,
  pausedAt,
  busy,
  onReconnect,
  onContinue,
}: {
  detail: string | null | undefined
  pausedAt: string | null | undefined
  busy: boolean
  onReconnect: () => void
  onContinue: () => void
}): JSX.Element {
  /**
   * ONB-10. S-D2 draws "checkpoint/challenge · paused 14:22 IST · 0 retries" and
   * the app drew the first third, when there was one — `last_error` alone, and
   * nothing at all when the provider had not set it.
   *
   * **"0 retries" is a literal, and it is the honest one.** It is not a counter
   * the code is missing: CLAUDE.md hard-enforces *"Auto-pause on any challenge.
   * Never retry through one"*, `app/db/linkedin.mark_challenged` is never
   * reversed except by a person, and `app/linkedin/pause.py` argues why there is
   * not even a background check. Zero is what the system does, so the line states
   * it rather than reading a field that could only ever be zero.
   *
   * `paused_at` comes from the connection row's `last_checked_at` while the
   * status is `challenge` — see `ConnectionOut.of`. Each part is dropped when it
   * is unknown rather than printed as a blank, so the line reads as a shorter
   * sentence rather than a broken one.
   */
  const parts = [detail, pausedAt ? `paused ${pausedClock(pausedAt) ?? ''}`.trim() : null]
    .filter((part): part is string => Boolean(part))
    .concat('0 retries')
    .join(' · ')

  return (
    <>
      <section className="ar-card">
        <div className="ar-card__body">
          <div className="ar-error-state" role="alert">
            <div className="ar-error-state__icon" aria-hidden="true">
              !
            </div>
            <p className="ar-error-state__title">LinkedIn asked for a security check</p>
            <p className="ar-error-state__body">
              We stopped immediately and did not try again — retrying through a checkpoint is what
              gets accounts banned. Open LinkedIn in your own browser, complete the check, then come
              back and reconnect.
            </p>
            <p className="ar-error-state__detail">{parts}</p>
            <div className="ar-btn-group" style={{ justifyContent: 'center' }}>
              <Button
                tone="primary"
                size="sm"
                busy={busy}
                busyLabel="Reconnecting"
                onClick={onReconnect}
              >
                I&rsquo;ve completed it — reconnect
              </Button>
              <Button size="sm" onClick={onContinue}>
                Continue without LinkedIn
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="ar-alert ar-alert--info">
        <span className="ar-alert__icon" aria-hidden="true">
          ◷
        </span>
        <div className="ar-alert__body">
          <strong>Everything else keeps running</strong>A LinkedIn pause never stops your SEO work.
          Audits, content, and publishing carry on.
        </div>
      </div>
    </>
  )
}
